import { useMemo } from 'react';
import { Bookmark, Pin, PinOff, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSavedViews } from '@/state/savedViews';
import { fmt } from '@/lib/format';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

/** Manage the filter combinations saved from the topbar. */
export function SavedViewsPage() {
  const sorted = useSavedViews((s) => s.sorted);
  const updateView = useSavedViews((s) => s.update);
  const removeView = useSavedViews((s) => s.remove);
  const views = sorted();
  const pinnedCount = useMemo(() => views.filter((v) => v.pinned).length, [views]);

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Workspace"
        title="Saved views"
        description="Filter combinations saved from the topbar. Pinned views appear first in the picker."
        actions={
          <Badge tone="neutral" className="gap-1">
            <Bookmark className="h-3 w-3" aria-hidden />
            {views.length} total
            {pinnedCount > 0 ? ` · ${pinnedCount} pinned` : ''}
          </Badge>
        }
      />

      <Card>
        <CardBody className="text-sm">
          {views.length === 0 ? (
            <EmptyState
              icon={<Bookmark className="h-5 w-5" />}
              title="No saved views yet"
              description="Set up filters on any page, then save them from the topbar to come back in one click."
              withTip
            />
          ) : (
            <ul className="space-y-1.5">
              {views.map((view) => (
                <li
                  key={view.id}
                  className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2"
                >
                  <Bookmark
                    className={cn('h-3.5 w-3.5 shrink-0', view.pinned ? 'text-primary' : 'text-muted-foreground')}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{view.name}</p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      saved {fmt.datetime(view.updated_at)} ·{' '}
                      <code className="font-mono">{view.query}</code>
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => updateView(view.id, { pinned: !view.pinned })}
                    aria-label={view.pinned ? `Unpin ${view.name}` : `Pin ${view.name}`}
                  >
                    {view.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      removeView(view.id);
                      notify.message(`Removed "${view.name}"`, { duration: 1500 });
                    }}
                    aria-label={`Delete ${view.name}`}
                    className="text-muted-foreground hover:text-danger"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
